import React from 'react';
import classnames from 'classnames/bind';
import { isEmpty } from 'lodash';
import CommonCollapsibleCard from 'components/CommonCollapsibleCard/CommonCollapsibleCard';
import { INTEGRATION_TYPE, PAGE_VIEW_EVENT_NAME } from 'modules/PixelTester/config/pixelTesterConsts';
import {
    COMMON_FLAGS,
    FEATURE_FLAGS,
    useCommonConfig,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { CONVERSION_TYPE } from 'modules/tracking/config';
import { EventCardHeader } from './components/EventCardHeader/EventCardHeader';
import { EventData } from './components/EventData/EventData';
import { UnrecognisedEventDescription } from './components/UnrecognisedEventDescription/UnrecognisedEventDescription';
import styles from './event.module.scss';

const classNameBuilder = classnames.bind(styles);

export const Event = ({ eventData, highlightedKeys, isOnBoarding }) => {
    const { [COMMON_FLAGS.PIXEL_TESTER_DEFAULT_SHOPIFY_EVENTS]: defaultShopifyEvents } = useCommonConfig([
        COMMON_FLAGS.PIXEL_TESTER_DEFAULT_SHOPIFY_EVENTS,
    ]);
    const isUserMatchingEnabled = useConfigMatch({ [FEATURE_FLAGS.PIXEL_TESTER_USER_MATCHING_ENABLED]: 'true' });
    const isUserMatchingPermitted = usePermissions(['REALIZE_PIXEL_TESTER_USER_MATCHING']);

    if (!eventData) {
        return null;
    }
    const { id, eventName, matchedRules = [], pixelIntegrationType } = eventData;
    const eventType = matchedRules?.[0]?.type ?? CONVERSION_TYPE.EVENT_BASED;
    const conversionName = matchedRules?.[0]?.conversionName;
    const hasConversions = !isEmpty(matchedRules);
    const isDefaultShopifyEvent =
        pixelIntegrationType === INTEGRATION_TYPE.SHOPIFY_APP && defaultShopifyEvents.includes(eventName);
    const isDefaultPageViewEvent = eventName === PAGE_VIEW_EVENT_NAME && !hasConversions;
    const isAutomaticallyFired = isDefaultShopifyEvent || isDefaultPageViewEvent;
    const showDescription = !hasConversions && !isAutomaticallyFired;
    const isHighlighted = highlightedKeys?.has?.(eventData._key) ?? false;
    const showUserMatching = isUserMatchingEnabled && isUserMatchingPermitted && !isDefaultShopifyEvent;

    return (
        <CommonCollapsibleCard
            bodyClassName={styles['card-body']}
            headerClassName={classNameBuilder('card-header', { highlighted: isHighlighted })}
            containerClassName={styles['card-container']}
            descriptionClassName={styles['description']}
            id={`PIXEL_TESTER_EVENT_${conversionName}_${id}`}
            isInitiallyCollapsed
            header={
                <EventCardHeader
                    conversionName={conversionName}
                    eventType={eventType}
                    eventData={eventData}
                    isAutomaticallyFired={isAutomaticallyFired}
                />
            }
            description={
                showDescription ? (
                    <UnrecognisedEventDescription eventData={eventData} isOnBoarding={isOnBoarding} />
                ) : null
            }
        >
            <EventData eventData={eventData} eventType={eventType} showUserMatching={showUserMatching} />
        </CommonCollapsibleCard>
    );
};
